import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { clsx } from 'clsx';
import { $getTableCellNodeFromLexicalNode, $isTableCellNode, TableCellNode } from '@lexical/table';
import {
  $getSelection,
  $isRangeSelection,
  $getRoot,
  KEY_DOWN_COMMAND,
  $isNodeSelection,
  COMMAND_PRIORITY_CRITICAL,
} from 'lexical';
import { $getNearestNodeOfType } from '@lexical/utils';
import { useRef, useEffect, useImperativeHandle, forwardRef } from 'react';

import type { TableNode } from '@lexical/table';
import type { JSX } from 'react';
import type { TextNode } from 'lexical';
import type { addTableForm } from './AddTableFrom';
import type { Actions } from './TableCellContextMenu';

import { AddTableForm } from './AddTableFrom';
import {
  createTable,
  DEFAULT_COL_WIDTH,
  DEFAULT_EDITOR_WIDTH,
  deleteColumn,
  deleteRow,
  deleteTable,
  getEditorContentWidth,
  getEditorParrentCellWidth,
  insertColumn,
  insertRow,
  PREVENT_OPEN_DELAY,
} from './helpers';

import { $createTableWrapperNode } from 'components/RteEditor/nodes';
import { useFlyout } from 'components/RteEditor/hooks/useFlyout';
import { Icon } from 'components';
import styles from 'components/RteEditor/RteEditor.module.css';
import { useTranslation } from 'components/RteEditor/hooks/useTranslation';

interface TablePluginProps {
  isActive: boolean;
}
export interface TableMutation {
  mutateTable: (type: Actions) => void;
}

export const TablePlugin = forwardRef(({ isActive }: TablePluginProps, ref): JSX.Element => {
  const t = useTranslation();
  const [editor] = useLexicalComposerContext();
  const { flyout, showFlyout, hideFlyout, isVisible } = useFlyout();
  const addTableForm = useRef<addTableForm>(undefined);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const preventOpen = useRef(false);
  const isVisibleRef = useRef(isVisible);

  const openFlyout = () => {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (!preventOpen.current && rect) {
      showFlyout({ position: { top: rect.bottom, left: rect.left }, ...flyoutOptions });
    }
  };

  const closeFlyout = () => {
    hideFlyout();
  };

  const insertTable = (nrOfRows: number, nrOfColumns: number) => {
    editor.update(() => {
      const selection = $getSelection();
      const tableNode = createTable(nrOfRows, nrOfColumns);

      const anchorNode =
        ($isRangeSelection(selection) && selection.anchor.getNode()) ||
        ($isNodeSelection(selection) && selection.getNodes()[0]) ||
        null;

      const willBeNested = anchorNode ? $getNearestNodeOfType(anchorNode, TableCellNode) : null;

      const availibleWidth = willBeNested
        ? getEditorParrentCellWidth(willBeNested, nrOfColumns, editor)
        : getEditorContentWidth(editor) || DEFAULT_EDITOR_WIDTH;

      const perCol = Math.max(DEFAULT_COL_WIDTH, Math.floor(availibleWidth / Math.max(1, nrOfColumns)));

      const colWidths = Array.from({ length: nrOfColumns }, () => perCol);

      (tableNode as TableNode).setColWidths(colWidths);

      const tableWrapper = $createTableWrapperNode();
      tableWrapper.append(tableNode);
      if ($isRangeSelection(selection)) {
        selection.insertNodes([tableWrapper]);
      } else {
        $getRoot().append(tableWrapper);
      }
      const firstDescendant = tableWrapper.getFirstDescendant();
      if (firstDescendant) {
        (firstDescendant as TextNode).select();
      }
    });

    closeFlyout();
  };

  const mutateTable = (action: Actions) => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        const anchorNode = selection.anchor.getNode();
        const currentNode = $getTableCellNodeFromLexicalNode(anchorNode);

        if ($isTableCellNode(currentNode)) {
          switch (action) {
            case 'insertRowAbove':
            case 'insertRowBelow':
              insertRow(currentNode, action);
              break;
            case 'deleteRow':
              deleteRow(currentNode);
              break;
            case 'insertColumnLeft':
            case 'insertColumnRight':
              insertColumn(currentNode, action);
              break;
            case 'deleteColumn':
              deleteColumn(currentNode);
              break;
            case 'deleteTable':
              deleteTable(currentNode);
              break;
          }
        }
      }
    });
  };

  const flyoutOptions = {
    children: (
      <AddTableForm
        onCloseHandler={closeFlyout}
        onSaveHandler={(rows, columns) => insertTable(rows, columns)}
        ref={addTableForm}
      />
    ),
  };

  useEffect(() => {
    if (!isVisible) {
      addTableForm.current?.clearForm();
      setTimeout(() => (preventOpen.current = false), PREVENT_OPEN_DELAY);
    } else {
      preventOpen.current = true;
      addTableForm.current?.focusFirstField();
    }
  }, [addTableForm, isVisible]);

  useEffect(() => {
    const removeKeyboardShortcut = editor.registerCommand(
      KEY_DOWN_COMMAND,
      (event: KeyboardEvent) => {
        if (event.ctrlKey && event.shiftKey && event.code === 'Digit9') {
          event.preventDefault();
          openFlyout();
          return true;
        }
        return false;
      },
      COMMAND_PRIORITY_CRITICAL
    );

    return () => {
      removeKeyboardShortcut();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor]);

  useEffect(() => {
    isVisibleRef.current = isVisible;
  }, [isVisible]);

  useEffect(() => {
    const removeKeydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isVisibleRef.current) {
        event.preventDefault();
        event.stopPropagation();
        closeFlyout();
      }
    };

    document.addEventListener('keydown', removeKeydown, { capture: true });

    return () => {
      document.removeEventListener('keydown', removeKeydown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useImperativeHandle(ref, () => ({
    mutateTable,
  }));

  return (
    <div className={styles['editor__toolbar-button-group']}>
      <div className={styles['editor__toolbar-button-flyoutwrapper']}>
        <button
          onClick={openFlyout}
          title={t.table.tableTooltip}
          className={clsx(styles['editor__toolbar-button'], isActive && styles[`editor__toolbar-button--active`])}
          aria-label={t.aria.labels.table}
          aria-keyshortcuts={t.aria.keyshortcuts.table}
          ref={buttonRef}
          type="button"
        >
          <Icon name="Table" size={24} />
        </button>
        {flyout}
      </div>
    </div>
  );
});
